/**
 * The schema × verb permission matrix.
 *
 * 🚨 Rows come from the `schemas` prop, which the caller loads from
 * `GET /api/roles/schemas`. Nothing here knows how many schemas exist. A schema
 * whose feature flags are all off is simply absent from that response — absence
 * IS the hiding, so this component has no "hidden" branch to get wrong.
 *
 * Pure and presentational: no hooks, no fetching, no contexts. That is what
 * lets it be screenshotted in isolation at 375px in both themes.
 *
 * Layout: a real table from `sm` up; stacked cards below it, because a 4-column
 * checkbox grid with row labels does not fit 375px without either horizontal
 * scroll or unreadable truncation.
 */
import React from 'react';
import { Lock, Eye } from 'lucide-react';
import {
  VERBS,
  WRITE_VERBS,
  getVerbSet,
  withImpliedView,
} from '../../types/rbac';
import type { RolePermissions, SchemaDescriptor, Verb, VerbSet } from '../../types/rbac';

interface RolePermissionMatrixProps {
  schemas: SchemaDescriptor[];
  permissions: RolePermissions;
  /** Omitted in read-only mode. */
  onChange?: (next: RolePermissions) => void;
  /** System presets are immutable — the whole grid renders disabled. */
  readOnly?: boolean;
}

const VERB_LABEL: Record<Verb, string> = {
  view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete',
};

const LOCK_REASON =
  'Admin/owner only — DNS records, email deliverability and public URLs leave the product.';

const RolePermissionMatrix: React.FC<RolePermissionMatrixProps> = ({
  schemas, permissions, onChange, readOnly = false,
}) => {
  const isLocked = (schema: SchemaDescriptor, verb: Verb) =>
    !!schema.locked_verbs?.includes(verb);

  const toggle = (schema: SchemaDescriptor, verb: Verb) => {
    if (readOnly || !onChange || isLocked(schema, verb)) return;
    const current = getVerbSet(permissions, schema.key);
    const next: VerbSet = { ...current, [verb]: !current[verb] };
    // add/edit/delete imply view — tick view in the UI so what the admin sees
    // matches what the backend will enforce on write.
    onChange({ ...permissions, [schema.key]: withImpliedView(next) });
  };

  /** `view` is forced on (and locked) while any write verb is granted. */
  const viewImplied = (verbs: VerbSet) => WRITE_VERBS.some((v) => verbs[v]);

  const cellId = (s: string, v: Verb) => `perm-${s}-${v}`;

  const Checkbox: React.FC<{ schema: SchemaDescriptor; verb: Verb }> = ({ schema, verb }) => {
    const verbs = getVerbSet(permissions, schema.key);
    const locked = isLocked(schema, verb);
    const impliedOn = verb === 'view' && viewImplied(verbs);
    const disabled = readOnly || locked || impliedOn;
    const title = locked
      ? LOCK_REASON
      : impliedOn
        ? 'View is required by the Add / Edit / Delete grants below.'
        : undefined;

    return (
      <span className="inline-flex items-center justify-center">
        <input
          id={cellId(schema.key, verb)}
          type="checkbox"
          checked={verbs[verb]}
          disabled={disabled}
          title={title}
          aria-label={`${VERB_LABEL[verb]} ${schema.label}`}
          onChange={() => toggle(schema, verb)}
          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/50 accent-purple-600 disabled:opacity-40 disabled:cursor-not-allowed"
        />
        {locked && <Lock className="w-3 h-3 ml-1 text-amber-600 dark:text-amber-400" aria-hidden="true" />}
      </span>
    );
  };

  const lockedSchemas = schemas.filter((s) => s.locked_verbs?.length);

  return (
    <div>
      {/* ── Desktop / tablet: real table ─────────────────────────────────── */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-2 pr-3 font-semibold text-gray-700 dark:text-gray-300">
                Page
              </th>
              {VERBS.map((v) => (
                <th key={v} className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300 text-center w-16">
                  {VERB_LABEL[v]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schemas.map((schema) => (
              <tr key={schema.key} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                <td className="py-2 pr-3 text-gray-800 dark:text-gray-200">
                  {schema.label}
                </td>
                {VERBS.map((v) => (
                  <td key={v} className="py-2 px-2 text-center">
                    <Checkbox schema={schema} verb={v} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 375px: stacked cards. A 5-column table cannot be read at this
             width; each schema becomes its own labelled block. ───────────── */}
      <div className="sm:hidden space-y-2">
        {schemas.map((schema) => (
          <div
            key={schema.key}
            className="rounded-xl border border-gray-200 dark:border-gray-700 p-3"
          >
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 break-words">
              {schema.label}
            </p>
            <div className="grid grid-cols-2 gap-y-2 gap-x-2">
              {VERBS.map((v) => (
                <label
                  key={v}
                  htmlFor={cellId(schema.key, v)}
                  className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 min-w-0"
                >
                  <Checkbox schema={schema} verb={v} />
                  <span className="truncate">{VERB_LABEL[v]}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {schemas.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
          No pages available to configure.
        </p>
      )}

      {/* 🚨 The reason locked verbs are disabled must be VISIBLE. An admin who
          sees a greyed checkbox with no explanation files it as a bug. */}
      {lockedSchemas.length > 0 && (
        <p className="mt-3 flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            {lockedSchemas.map((s) => s.label).join(', ')}:{' '}
            {lockedSchemas[0].locked_verbs?.map((v) => VERB_LABEL[v]).join(' and ')}{' '}
            can&apos;t be granted to a custom role. {LOCK_REASON}
          </span>
        </p>
      )}

      <p className="mt-2 flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>Add, Edit and Delete each require View, so View is ticked automatically.</span>
      </p>
    </div>
  );
};

export default RolePermissionMatrix;
